import { UserProfile } from "@/lib/storage";
import { User, Briefcase, GraduationCap, Wrench, Mail, Phone, MapPin, Linkedin, Globe, Pencil, LogIn, LogOut, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Props {
  profile: UserProfile;
  onEditDetails?: () => void;
  isSignedIn?: boolean;
  onSignInOut?: () => void;
  showExtendedFields?: boolean;
}

export default function ProfileTab({ profile, onEditDetails, isSignedIn = false, onSignInOut, showExtendedFields = false }: Props) {
  const { personal } = profile;
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [selectedExp, setSelectedExp] = useState<typeof profile.experience[0] | null>(null);
  const [selectedEdu, setSelectedEdu] = useState<typeof profile.education[0] | null>(null);

  return (
    <div className="p-5 space-y-5 animate-fade-up">
      {/* Personal Card */}
      <div className="rounded-xl bg-gradient-to-br from-primary to-primary/80 p-4 text-primary-foreground">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-primary-foreground/20 flex items-center justify-center">
            <User className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg">{personal.fullName || 'Your Name'}</h3>
            <p className="text-sm opacity-80">{personal.location || 'Location'}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs opacity-90">
          {personal.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{personal.email}</div>}
          {personal.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{personal.phone}</div>}
          {personal.linkedIn && <a href={personal.linkedIn} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 underline underline-offset-2 hover:opacity-70 transition-opacity"><Linkedin className="w-3 h-3" />LinkedIn</a>}
          {personal.portfolio && <a href={personal.portfolio} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 underline underline-offset-2 hover:opacity-70 transition-opacity"><Globe className="w-3 h-3" />Portfolio</a>}
        </div>
      </div>

      {showExtendedFields && (
        <div className="rounded-xl border border-border bg-card p-3">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Autofill Extras</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {personal.city && <div><span className="text-muted-foreground">City:</span> {personal.city}</div>}
            {personal.state && <div><span className="text-muted-foreground">State:</span> {personal.state}</div>}
            {personal.country && <div><span className="text-muted-foreground">Country:</span> {personal.country}</div>}
            {personal.postcode && <div><span className="text-muted-foreground">Postcode:</span> {personal.postcode}</div>}
            {personal.nationality && <div><span className="text-muted-foreground">Nationality:</span> {personal.nationality}</div>}
            {personal.seniorityLevel && <div><span className="text-muted-foreground">Seniority:</span> {personal.seniorityLevel}</div>}
            {personal.timeZone && <div className="col-span-2"><span className="text-muted-foreground">Time zone:</span> {personal.timeZone}</div>}
          </div>
        </div>
      )}

      {/* Action buttons - below profile card */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="gap-1.5 text-xs" onClick={onEditDetails}>
          <Pencil className="w-3.5 h-3.5" /> Edit Details
        </Button>
        <Button
          type="button"
          variant="outline"
          className="gap-1.5 text-xs disabled:opacity-50"
          onClick={onSignInOut}
          disabled={!isSignedIn}
          title={!isSignedIn ? "Sign in from Settings or the banner after you create an account" : undefined}
        >
          {isSignedIn ? (
            <><LogOut className="w-3.5 h-3.5" /> Sign Out</>
          ) : (
            <><LogIn className="w-3.5 h-3.5" /> Sign In</>
          )}
        </Button>
      </div>

      {/* Summary */}
      {profile.summary && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Summary</h4>
          <p className={`text-sm text-foreground leading-relaxed ${!summaryExpanded ? 'line-clamp-2' : ''}`}>{profile.summary}</p>
          {profile.summary.length > 80 && (
            <button onClick={() => setSummaryExpanded(!summaryExpanded)} className="text-xs text-primary font-medium mt-1 flex items-center gap-0.5 hover:underline">
              {summaryExpanded ? <><ChevronUp className="w-3 h-3" /> See less</> : <><ChevronDown className="w-3 h-3" /> See more</>}
            </button>
          )}
        </div>
      )}

      {/* Experience */}
      {profile.experience.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5" /> Experience
          </h4>
          <div className="space-y-3">
            {profile.experience.map(exp => (
              <div key={exp.id} className="p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors" onClick={() => setSelectedExp(exp)}>
                <div className="font-medium text-sm">{exp.title}</div>
                <div className="text-xs text-muted-foreground">{exp.company} · {exp.startDate} – {exp.endDate || 'Present'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education */}
      {profile.education.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5" /> Education
          </h4>
          <div className="space-y-3">
            {profile.education.map(edu => (
              <div key={edu.id} className="p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors" onClick={() => setSelectedEdu(edu)}>
                <div className="font-medium text-sm">{edu.degree} in {edu.field}</div>
                <div className="text-xs text-muted-foreground">{edu.institution} · {edu.graduationYear}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills */}
      {profile.skills.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Wrench className="w-3.5 h-3.5" /> Skills
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {profile.skills.map(skill => (
              <span key={skill} className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">{skill}</span>
            ))}
          </div>
        </div>
      )}

      {/* Experience Detail Dialog */}
      <Dialog open={!!selectedExp} onOpenChange={() => setSelectedExp(null)}>
        <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[400px] w-full">
          <DialogHeader>
            <DialogTitle>{selectedExp?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-muted-foreground">{selectedExp?.company} · {selectedExp?.startDate} – {selectedExp?.endDate || 'Present'}</div>
            {selectedExp?.description && <p className="text-foreground leading-relaxed whitespace-pre-wrap">{selectedExp.description}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Education Detail Dialog */}
      <Dialog open={!!selectedEdu} onOpenChange={() => setSelectedEdu(null)}>
        <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[400px] w-full">
          <DialogHeader>
            <DialogTitle>{selectedEdu?.degree} in {selectedEdu?.field}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">{selectedEdu?.institution} · {selectedEdu?.graduationYear}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
